import { pxToRem } from '@creditornot/cb-utils';

import { typographyCssNew } from './typographyCssNew';

const baseHeadingStyle = `
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
  font-variant-ligatures: common-ligatures;
  font-family: var(--cb-font-family-brand);
  font-feature-settings: var(--cb-font-family-brand-font-feature-settings);

`;

const baseBodyStyle = `
  font-feature-settings: 'kern';
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
  font-family: var(--cb-font-family-sans);
`;

export type FontWeight = 'inherit' | 'normal' | 'bold' | 'semiBold';

export const fontWeights: Record<FontWeight, string> = {
  inherit: 'inherit',
  normal: '400',
  semiBold: '500',
  bold: '700',
};

interface DecoratedFontStyleOptions {
  /** Do not add rules for nested `b` and `strong` elements. Use for example when applying font styles to pseudo-selectors.  */
  disableNestedWeightTags?: boolean;
  weight?: FontWeight;
}

function decoratedFontStyle(cssStyle: string) {
  return ({ disableNestedWeightTags, weight = 'normal' }: DecoratedFontStyleOptions = {}) => {
    // prettier-ignore
    return `
      ${cssStyle}
      font-weight: ${fontWeights[weight]};

      ${disableNestedWeightTags ? '' : `
        b, strong {
          font-weight: ${weight === 'bold' || weight === 'semiBold' ? '700' : '600'};
        }
      `}
    `;
  };
}

export const typographyCssOld = {
  /**
   * @deprecated This style is deprecated. If you still need to access the old text styles, please use `legacyToolsTypographyCss`.
   * Moreover, prefer using class based approach with `typographyClassName` for new features.
   */
  baseBodyStyle: () => baseBodyStyle,
  /**
   * @deprecated This style is deprecated. If you still need to access the old text styles, please use `legacyToolsTypographyCss`.
   * Moreover, prefer using class based approach with `typographyClassName` for new features.
   */
  baseHeadingStyle: () => baseHeadingStyle,
  /**
   * @deprecated This style is deprecated. If you still need to access the old text styles, please use `legacyToolsTypographyCss`.
   *
   * Possible replacement: `typographyClassName.display`.
   *
   * Moreover, prefer using class based approach with `typographyClassName` for new features.
   */
  Heading1: () => `
    ${baseHeadingStyle}
    font-size: ${pxToRem(48)};
    line-height: ${pxToRem(52)};
    font-weight: 600;
    font-style: normal;
    font-stretch: normal;
    text-transform: none;
  `,
  /**
   * @deprecated This style is deprecated. If you still need to access the old text styles, please use `legacyToolsTypographyCss`.
   *
   * Possible replacement: `typographyClassName.display`.
   *
   * Moreover, prefer using class based approach with `typographyClassName` for new features.
   */
  Heading2: () => `
    ${baseHeadingStyle}
    font-size: ${pxToRem(40)};
    line-height: ${pxToRem(44)};
    font-weight: 600;
    font-style: normal;
    font-stretch: normal;
    text-transform: none;
  `,
  /**
   * @deprecated This style is deprecated. If you still need to access the old text styles, please use `legacyToolsTypographyCss`.
   *
   * Possible replacement: `typographyClassName.largeTitle`.
   *
   * Moreover, prefer using class based approach with `typographyClassName` for new features.
   */
  Heading3: () => `
    ${baseHeadingStyle}
    font-size: ${pxToRem(32)};
    line-height: ${pxToRem(36)};
    font-weight: 600;
    font-style: normal;
    font-stretch: normal;
    text-transform: none;
  `,
  /**
   * @deprecated This style is deprecated. If you still need to access the old text styles, please use `legacyToolsTypographyCss`.
   *
   * Possible replacement: `typographyClassName.largeTitle`.
   *
   * Moreover, prefer using class based approach with `typographyClassName` for new features.
   */
  Heading4: () => `
    ${baseHeadingStyle}
    font-size: ${pxToRem(28)};
    line-height: ${pxToRem(32)};
    font-weight: 600;
    font-style: normal;
    font-stretch: normal;
    text-transform: none;
  `,
  /**
   * @deprecated This style is deprecated. If you still need to access the old text styles, please use `legacyToolsTypographyCss`.
   *
   * Possible replacement: `typographyClassName.title`.
   *
   * Moreover, prefer using class based approach with `typographyClassName` for new features.
   */
  Heading5: () => `
    ${baseHeadingStyle}
    font-size: ${pxToRem(24)};
    line-height: ${pxToRem(28)};
    font-weight: 600;
    font-style: normal;
    font-stretch: normal;
    text-transform: none;
  `,
  /**
   * @deprecated This style is deprecated. If you still need to access the old text styles, please use `legacyToolsTypographyCss`.
   *
   * Possible replacement: `typographyClassName.headline`.
   *
   * Moreover, prefer using class based approach with `typographyClassName` for new features.
   */
  Heading6: () => `
    ${baseHeadingStyle}
    font-size: ${pxToRem(20)};
    line-height: ${pxToRem(24)};
    font-weight: 600;
    font-style: normal;
    font-stretch: normal;
    text-transform: none;
  `,
  /**
   * @deprecated This style is deprecated. If you still need to access the old text styles, please use `legacyToolsTypographyCss`.
   *
   * Possible replacement: `typographyClassName.subheadline`.
   *
   * Moreover, prefer using class based approach with `typographyClassName` for new features.
   */
  Title1: () => `
    ${baseBodyStyle}
    font-size: ${pxToRem(16)};
    line-height: ${pxToRem(20)};
    font-weight: bold;
    font-style: normal;
    font-stretch: normal;
  `,
  /**
   * @deprecated This style is deprecated. If you still need to access the old text styles, please use `legacyToolsTypographyCss`.
   *
   * Possible replacement: `typographyClassName.captionLabel`.
   *
   * Moreover, prefer using class based approach with `typographyClassName` for new features.
   */
  Title2: () => `
    ${baseBodyStyle}
    font-size: ${pxToRem(14)};
    line-height: ${pxToRem(20)};
    font-weight: bold;
    font-style: normal;
    font-stretch: normal;
  `,
  /**
   * @deprecated This style is deprecated. If you still need to access the old text styles, please use `legacyToolsTypographyCss`.
   *
   * Possible replacement: `typographyClassName.footnoteLabel`.
   *
   * Moreover, prefer using class based approach with `typographyClassName` for new features.
   */
  Subtitle: () => `
    ${baseBodyStyle}
    font-size: ${pxToRem(12)};
    line-height: ${pxToRem(16)};
    font-weight: bold;
    font-style: normal;
    font-stretch: normal;
  `,
  /**
   * @deprecated This style is deprecated. If you still need to access the old text styles, please use `legacyToolsTypographyCss`.
   *
   * Possible replacement: `typographyClassName.body` or `typographyClassName.bodyLabel`.
   *
   * Moreover, prefer using class based approach with `typographyClassName` for new features.
   */
  Body1: decoratedFontStyle(`
    ${baseBodyStyle}
    font-size: ${pxToRem(18)};
    line-height: ${pxToRem(24)};
    font-style: normal;
    font-stretch: normal;
  `),
  /**
   * @deprecated This style is deprecated. If you still need to access the old text styles, please use `legacyToolsTypographyCss`.
   *
   * Possible replacement: `typographyClassName.body` or `typographyClassName.bodyLabel`.
   *
   * Moreover, prefer using class based approach with `typographyClassName` for new features.
   */
  Body2: decoratedFontStyle(`
    ${baseBodyStyle}
    font-size: ${pxToRem(16)};
    line-height: ${pxToRem(20)};
    font-style: normal;
    font-stretch: normal;
  `),
  /**
   * @deprecated This style is deprecated. If you still need to access the old text styles, please use `legacyToolsTypographyCss`.
   *
   * Possible replacement: `typographyClassName.caption` or `typographyClassName.captionLabel`.
   *
   * Moreover, prefer using class based approach with `typographyClassName` for new features.
   */
  Body3: decoratedFontStyle(`
    ${baseBodyStyle}
    font-size: ${pxToRem(14)};
    line-height: ${pxToRem(20)};
    font-style: normal;
    font-stretch: normal;
  `),
  /**
   * @deprecated This style is deprecated. If you still need to access the old text styles, please use `legacyToolsTypographyCss`.
   *
   * Possible replacement: `typographyClassName.footnote` or `typographyClassName.footnoteLabel`.
   *
   * Moreover, prefer using class based approach with `typographyClassName` for new features.
   */
  Small: decoratedFontStyle(`
    ${baseBodyStyle}
    font-size: ${pxToRem(12)};
    line-height: ${pxToRem(16)};
    font-weight: normal;
    font-style: normal;
    font-stretch: normal;
  `),
  /**
   * @deprecated This style is deprecated. If you still need to access the old text styles, please use `legacyToolsTypographyCss`.
   *
   * Possible replacement: `typographyClassName.footnote` or `typographyClassName.footnoteLabel`.
   *
   * Moreover, prefer using class based approach with `typographyClassName` for new features.
   */
  Tiny: decoratedFontStyle(`
    ${baseBodyStyle}
    font-size: ${pxToRem(10)};
    line-height: ${pxToRem(12)};
    font-weight: normal;
    font-style: normal;
    font-stretch: normal;
  `),
  /**
   * @deprecated This style is deprecated. If you still need to access the old text styles, please use `legacyToolsTypographyCss`.
   * Moreover, prefer using class based approach with `typographyClassName` for new features.
   */
  Overline1: () => `
    ${baseBodyStyle}
    text-transform: uppercase;
    font-size: ${pxToRem(16)};
    line-height: ${pxToRem(20)};
    font-weight: 500;
    font-style: normal;
    font-stretch: normal;
  `,
  /**
   * @deprecated This style is deprecated. If you still need to access the old text styles, please use `legacyToolsTypographyCss`.
   * Moreover, prefer using class based approach with `typographyClassName` for new features.
   */
  Overline2: () => `
    ${baseBodyStyle}
    text-transform: uppercase;
    font-size: ${pxToRem(14)};
    line-height: ${pxToRem(20)};
    font-weight: 500;
    font-style: normal;
    font-stretch: normal;
  `,
  /**
   * @deprecated This style is deprecated. If you still need to access the old text styles, please use `legacyToolsTypographyCss`.
   * Moreover, prefer using class based approach with `typographyClassName` for new features.
   */
  Overline3: () => `
    ${baseBodyStyle}
    text-transform: uppercase;
    font-size: ${pxToRem(12)};
    line-height: ${pxToRem(16)};
    font-weight: 500;
    font-style: normal;
    font-stretch: normal;
  `,
  /**
   * @deprecated This style is deprecated. If you still need to access the old text styles, please use `legacyToolsTypographyCss`.
   * Moreover, prefer using class based approach with `typographyClassName` for new features.
   */
  Overline4: () => `
    ${baseBodyStyle}
    text-transform: uppercase;
    font-size: ${pxToRem(10)};
    line-height: ${pxToRem(12)};
    font-weight: 500;
    font-style: normal;
    font-stretch: normal;
  `,
  /**
   * @deprecated This style is deprecated. If you still need to access the old text styles, please use `legacyToolsTypographyCss`.
   * Moreover, prefer using class based approach with `typographyClassName` for new features.
   */
  Quote1: () => `
    ${baseBodyStyle}
    font-size: ${pxToRem(16)};
    line-height: ${pxToRem(20)};
    font-weight: 500;
    font-style: italic;
    font-stretch: normal;
  `,
  /**
   * @deprecated This style is deprecated. If you still need to access the old text styles, please use `legacyToolsTypographyCss`.
   * Moreover, prefer using class based approach with `typographyClassName` for new features.
   */
  Quote2: () => `
    ${baseBodyStyle}
    font-size: ${pxToRem(14)};
    line-height: ${pxToRem(20)};
    font-weight: 500;
    font-style: italic;
    font-stretch: normal;
  `,
} satisfies Record<string, () => string>;

export const typographyCss: typeof typographyCssNew & typeof typographyCssOld = {
  ...typographyCssOld,
  ...typographyCssNew,
};
